import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Divider,
  Badge,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AppsIcon from "@mui/icons-material/Apps";
import Menu from "@mui/material/Menu";
import LockIcon from "@mui/icons-material/Lock";
import SubscriptionModal from "../../Reusable/SubscriptionModal";

import OruclicksLogo from "../../Reusable/Logo";
import MenuList from "./MenuList"; // Import the MenuList component
import { decodeJWT, getAuthTokenPayload } from "../../../Utils";
import appUIConfigs from "./appUIConfigs";

const ResponsiveAppBar = ({ isDarkTheme }) => {
  // Add responsive breakpoints - ONLY for header
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = useState(false); // To control logout confirmation dialog
  const [drawerOpen, setDrawerOpen] = useState(false); // To control the side menu
  const [currentTime, setCurrentTime] = useState(new Date());
  const [remainingTime, setRemainingTime] = useState(10 * 60 * 60); // 10 hours in seconds
  const [logoutReason, setLogoutReason] = useState("Break"); // keep default only
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Loading state for logout process
  const [appMenuAnchor, setAppMenuAnchor] = useState(null); // For app menu
  const [userProduct, setUserProduct] = useState(null); // Store decoded user product permissions
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const appMenuOpen = Boolean(appMenuAnchor);

  // Retrieve login time from localStorage
  const loginTime = localStorage.getItem("loginTime");
  const startTime = loginTime ? new Date(parseInt(loginTime, 10)) : new Date(); // Parse loginTime to Date
  const endTime = new Date(startTime.getTime() + 10 * 60 * 60 * 1000); // Calculate end time (10 hours from start time)

  const navigate = useNavigate();
  const location = useLocation();

  // User information - properly defined
  const userId = getAuthTokenPayload()?.userId;
  const clientId = getAuthTokenPayload()?.clientId;
  const roleName = getAuthTokenPayload()?.roleName;
  const userName = getAuthTokenPayload()?.userName;
  const roleId = getAuthTokenPayload()?.roleId;
  const userStatus = getAuthTokenPayload()?.userStatus;
  const profilePicture = getAuthTokenPayload()?.profilePicture;
  const planId = getAuthTokenPayload()?.planId;
  const isAdmin = roleName?.toLowerCase() === "admin";

  const planIdMap = {
    "oruclicks-bus-plan-003": "Business",
    "oruclicks-prof-plan-002": "Professional",
    "oruclicks-star-plan-001": "Starter",
  };
  const planName = planIdMap[planId] || planId;

  const handleLogoClick = () => {
    const isLoggedIn = localStorage.getItem("isAuthenticated");

    if (isLoggedIn) {
      // Get menuList from JWT auth token
      const menuList = getAuthTokenPayload()?.menuList ?? [];

      // Find first permitted menu item (with permission: "yes")
      const defaultMenu = menuList.find((menu) => menu.permission === "yes");

      if (defaultMenu) {
        // Map menuId to route path
        const menuToRoutePath = {
          "MENU-001": "/dashboard",
          "MENU-002": "/leads",
          "MENU-003": "/users",
        };

        // Navigate to the mapped route or fallback to dashboard
        const route = menuToRoutePath[defaultMenu.menuId] || "/dashboard";
        navigate(route);
      } else {
        // If no permitted menus, go to dashboard
        navigate("/dashboard");
      }
    } else {
      // If not logged in, go to login page
      navigate("/");
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen); // Toggle the drawer open/close state
  };

  const handleMenuItemClick = (path) => {
    // If user status is Interested, show subscription modal
    if (userStatus === "Interested") {
      setDrawerOpen(false);
      setSubscriptionModalOpen(true);
      return;
    }
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogoutClick = () => {
    setLogoutReason("Break");
    setOpenDialog(true);
  };

  const handleSupportClick = () => {
    // Get auth token from localStorage (set by protectedRoute)
    const authToken = localStorage.getItem('authToken');
    
    // Build URL with token as query parameter
    let supportUrl = 'https://support.oruclicks.com/dashboard';
    
    if (authToken) {
      supportUrl += '?token=' + authToken;
    }
    
    // Open in new tab
    window.open(supportUrl, '_blank');
  };

  // Unified method to handle all app navigation
  const handleAppNavigation = (appUrl) => {
    if (!appUrl) return;
    
    // Get auth token from localStorage (set by protectedRoute)
    const authToken = localStorage.getItem('authToken');
    
    // Build URL with token as query parameter
    let targetUrl = appUrl;
    
    if (authToken) {
      targetUrl += '?token=' + authToken;
    }
    
    // Open in new tab
    window.open(targetUrl, '_blank');
  };

  const handleAppMenuClick = (event) => {
    setAppMenuAnchor(event.currentTarget);
  };

  const handleAppMenuClose = () => {
    setAppMenuAnchor(null);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      const userId = getAuthTokenPayload()?.userId;
      await fetch("http://127.0.0.1:3000/user-manage-api/Staff/LogoutReason", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          reason: logoutReason || "Break", // send silently
        }),
      });
      localStorage.clear();
      setOpenDialog(false);
      setIsLoggingOut(false);
      window.location.href = "https://www.oruclick.com/";
    } catch (error) {
      console.error("Error during logout:", error);
      localStorage.clear();
      setOpenDialog(false);
      setIsLoggingOut(false);
      window.location.href = "https://www.oruclick.com/";
    }
  };

  const handleLogoutCancel = () => {
    setOpenDialog(false);
    setLogoutReason("Break");
    // setError(false); // removed
  };

  const isLoginPage = location.pathname === "/";

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Update remaining time every second
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      const now = new Date(); // Get the current time
      const timeLeft = Math.max(0, Math.floor((endTime - now) / 1000)); // Calculate remaining time in seconds
      setRemainingTime(timeLeft);

      // If the countdown reaches zero, clear the interval
      if (timeLeft <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    return () => clearInterval(countdownInterval); // Cleanup interval on component unmount
  }, [endTime]);

  // Decode JWT token to get user product permissions
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      const decodedToken = decodeJWT(authToken);
      if (decodedToken && decodedToken.userProduct) {
        setUserProduct(decodedToken.userProduct);
      }
    }
  }, []);

  // Format date in DD-MMM-YYYY format
  const formatDate = (date) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format time in HH:MM:SS AM/PM format
  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  // Format remaining time in HH:MM:SS
  // const formatRemainingTime = (seconds) => {
  //   const hours = Math.floor(seconds / 3600);
  //   const minutes = Math.floor((seconds % 3600) / 60);
  //   const secs = seconds % 60;
  //   return `${hours.toString().padStart(2, "0")}:${minutes
  //     .toString()
  //     .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  // };

  // Calculate percentage of time elapsed
  // const calculateTimeProgress = () => {
  //   const totalDuration = 10 * 60 * 60; // 10 hours in seconds
  //   const elapsed = totalDuration - remainingTime;
  //   return Math.min(100, (elapsed / totalDuration) * 100);
  // };

  // Get color based on remaining time
  // const getTimeColor = () => {
  //   const percentage = calculateTimeProgress();
  //   if (percentage < 50) return isDarkTheme ? "#4caf50" : "#388e3c"; // Green
  //   if (percentage < 75) return isDarkTheme ? "#ff9800" : "#f57c00"; // Orange
  //   return isDarkTheme ? "#f44336" : "#d32f2f"; // Red
  // };

  // Get available apps based on user permissions
  const getAvailableApps = () => {
    if (!userProduct || !userProduct.showAppIcon || !userProduct.apps) {
      return [];
    }

    const availableApps = [];
    const { apps } = userProduct;

    // Add apps that have read or write permissions (not null)
    Object.keys(apps).forEach(appKey => {
      const appData = apps[appKey];
      const permission = appData?.permission;
      const url = appData?.url;
      // Only add apps with valid permissions and URLs
      if (permission && (permission === 'read' || permission === 'write') && url) {
        if (appUIConfigs[appKey]) {
          availableApps.push({
            ...appUIConfigs[appKey],
            permission,
            url,
          });
        }
      }
    });

    return availableApps;
  };

  // Get the available apps
  const availableApps = getAvailableApps();
  const shouldShowAppsButton = availableApps.length > 0;

  return (
    <Box sx={{ position: "relative" }}>
      <AppBar
        className="customize_header"
        position="static"
        sx={{
          background: isDarkTheme
            ? "linear-gradient(to right, #1a237e, #000000)"
            : "linear-gradient(to right, #ffffff, #f5f7fa)",
          color: isDarkTheme ? "#fff" : "#000",
          fontFamily: "DM Sans",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          borderRadius: "0 0 15px 15px",
        }}
      >
        <Container>
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              py: isMobile ? 0.25 : 0.5, // Reduce padding on mobile
            }}
          >
            {/* Left Section: Burger Menu and Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                gap: isMobile ? 1 : 2, // Reduce gap on mobile
              }}
            >
              {/* Conditionally Render Burger Menu */}
              {!isLoginPage && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  size={isMobile ? "small" : "medium"} // Smaller on mobile
                  sx={{
                    transition: "all 0.3s ease-in-out",
                    background: isDarkTheme
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.04)",
                    "&:hover": {
                      transform: "scale(1.05)",
                      background: isDarkTheme
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <MenuIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              )}

              {/* Logo */}
              <OruclicksLogo
                isDarkTheme={isDarkTheme}
                onClick={handleLogoClick}
                showText={true}
                size={isMobile ? "42px" : "45px"}
                textSize={isMobile ? "22px" : "24px"}
                iconOnly={false}
              />
            </Box>

            {/* Center Section: Contact Info & Time Display */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                flexGrow: 1,
                justifyContent: "center",
                gap: 3,
                mx: isMobile ? 0.5 : 2,
              }}
            >
              {/* Date & Time Display - Only when logged in and not mobile */}
              {!isLoginPage && !isMobile && (
                <>
                  {/* Date Display */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarTodayIcon
                      fontSize="small"
                      sx={{ color: isDarkTheme ? "#90caf9" : "#3949ab" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "600",
                        color: isDarkTheme ? "#fff" : "#000",
                        fontSize: "14px",
                      }}
                    >
                      {formatDate(currentTime)}
                    </Typography>
                  </Box>

                  {/* Current Time Display */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon
                      fontSize="small"
                      sx={{ color: isDarkTheme ? "#4caf50" : "#388e3c" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "600",
                        color: isDarkTheme ? "#fff" : "#000",
                        fontSize: "14px",
                      }}
                    >
                      {formatTime(currentTime)}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Right Section: Action Buttons */}
            <Box sx={{ flexShrink: 0, display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'center' }}>
              {/* App Menu - Based on user permissions */}
              {!isLoginPage && shouldShowAppsButton && (
                <>
                  <Badge
                    badgeContent={availableApps.length}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: '18px',
                        minWidth: '18px',
                        fontWeight: 700,
                      }
                    }}
                  >
                    <Button
                      onClick={handleAppMenuClick}
                      variant="contained"
                      startIcon={!isMobile ? <AppsIcon /> : null}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        borderRadius: "30px",
                        padding: isMobile ? "6px 12px" : "8px 20px",
                        minWidth: isMobile ? "auto" : "120px",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: 'relative',
                        overflow: 'hidden',
                        "&:hover": {
                          background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                        },
                        "&:before": {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                          transition: 'left 0.5s',
                        },
                        "&:hover:before": {
                          left: '100%',
                        },
                      }}
                    >
                      {isMobile ? <AppsIcon fontSize="small" /> : "Apps"}
                    </Button>
                  </Badge>
                  <Menu
                    anchorEl={appMenuAnchor}
                    open={appMenuOpen}
                    onClose={handleAppMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 280,
                        borderRadius: 3,
                        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                        background: isDarkTheme 
                          ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" 
                          : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                        border: isDarkTheme ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
                        overflow: 'visible',
                      }
                    }}
                  >
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          color: isDarkTheme ? "#94a3b8" : "#64748b",
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: '0.5px'
                        }}
                      >
                        Your Applications
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    {/* Grid view for apps: 2 per row */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1.5,
                      px: 2,
                      pb: 2,
                    }}>
                      {availableApps.map((app, index) => {
                        const AppIcon = app.icon;
                        return (
                          <Box
                            key={app.name}
                            onClick={() => {
                              handleAppMenuClose();
                              handleAppNavigation(app.url);
                            }}
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 2,
                              background: isDarkTheme ? 'transparent' : 'transparent',
                              transition: 'all 0.2s',
                              boxShadow: `0 2px 8px ${app.shadowColor}`,
                              p: 1.5,
                              '&:hover': {
                                background: app.hoverGradient,
                                transform: 'translateY(-2px) scale(1.04)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                background: app.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1,
                                boxShadow: `0 4px 12px ${app.shadowColor}`,
                              }}
                            >
                              <AppIcon sx={{ color: 'white', fontSize: 24 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.2, textAlign: 'center' }}>
                              {app.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDarkTheme ? '#94a3b8' : '#64748b', textAlign: 'center' }}>
                              {app.description}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Menu>
                </>
              )}

              {/* Support Button - Only visible when logged in */}
              {!isLoginPage && (
                <Button
                  variant="contained"
                  onClick={handleSupportClick}
                  startIcon={!isMobile ? <SupportAgentIcon /> : null}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: "30px",
                    padding: isMobile ? "6px 12px" : "8px 20px",
                    minWidth: isMobile ? "auto" : "120px",
                    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    boxShadow: "0 4px 10px rgba(59, 130, 246, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 15px rgba(59, 130, 246, 0.4)",
                    },
                  }}
                >
                  {isMobile ? <SupportAgentIcon fontSize="small" /> : "Support"}
                </Button>
              )}

              {/* Conditionally render Logout Button */}
              {!isLoginPage && (
                <Button
                  variant="contained"
                  onClick={handleLogoutClick}
                  startIcon={!isMobile ? <LogoutIcon /> : null} // Hide icon on mobile
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: "30px",
                    padding: isMobile ? "6px 12px" : "8px 20px", // Smaller padding on mobile
                    minWidth: isMobile ? "auto" : "120px", // Auto width on mobile
                    background: isDarkTheme
                      ? "linear-gradient(45deg, #f44336, #d32f2f)"
                      : "linear-gradient(45deg, #ef5350, #e53935)",
                    boxShadow: "0 4px 10px rgba(244, 67, 54, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: isDarkTheme
                        ? "linear-gradient(45deg, #d32f2f, #b71c1c)"
                        : "linear-gradient(45deg, #e53935, #c62828)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 15px rgba(244, 67, 54, 0.4)",
                    },
                  }}
                >
                  {isMobile ? <LogoutIcon fontSize="small" /> : "Logout"}
                </Button>
              )}

              {/* Conditionally render Login / Sign Up Button */}
              {isLoginPage && (
                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  startIcon={<LoginIcon fontSize={isMobile ? "small" : "medium"} />}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: "30px",
                    padding: isMobile ? "6px 14px" : "8px 22px",
                    fontSize: isMobile ? "13px" : "15px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 14px rgba(102, 126, 234, 0.45)",
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd6 0%, #6a3d9a 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.55)",
                    },
                  }}
                >
                  Login / Sign Up
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Side Drawer Menu - Keep Original Functionality */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: 280, // Keep original width
            background: isDarkTheme
              ? "linear-gradient(to bottom, #1a237e, #121212)"
              : "linear-gradient(to bottom, #ffffff, #f5f7fa)",
            color: isDarkTheme ? "#fff" : "#000",
            borderRadius: "0 15px 15px 0",
            padding: "0",
            boxShadow: "5px 0 20px rgba(0, 0, 0, 0.2)",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            top: 0,
            height: "100%",
          },
          zIndex: 1300,
        }}
      >
        {/* User Profile Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            background: isDarkTheme
              ? "linear-gradient(160deg, #1a237e 0%, #0d1547 100%)"
              : "linear-gradient(160deg, #3949ab 0%, #1a237e 100%)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
            px: 2,
            pt: 0,
            pb: 1.5,
            // Thin accent bar at very top
            borderTop: "3px solid",
            borderImageSlice: 1,
            borderImageSource: "linear-gradient(90deg, #667eea, #764ba2, #4caf50)",
          }}
        >
          {/* Avatar + Name + Role */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 1.6, mb: 1.4 }}>
            {/* Avatar with glowing ring */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: -2,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea, #764ba2, #4fc3f7)",
                  zIndex: 0,
                }}
              />
              <Avatar
                src={profilePicture || undefined}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: 46,
                  height: 46,
                  bgcolor: "#3949ab",
                  border: "2px solid #0d1547",
                  fontSize: "19px",
                  fontWeight: "700",
                  boxShadow: "0 4px 14px rgba(102,126,234,0.45)",
                }}
              >
                {!profilePicture && (userName?.charAt(0)?.toUpperCase() || "U")}
              </Avatar>
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "13.5px",
                  color: "#fff",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.1px",
                }}
              >
                {userName}
              </Typography>
              {roleName && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.45,
                    background: "linear-gradient(135deg, rgba(102,126,234,0.35), rgba(118,75,162,0.35))",
                    border: "1px solid rgba(255,255,255,0.15)",
                    color: "rgba(255,255,255,0.92)",
                    fontSize: "9.5px",
                    fontWeight: 700,
                    px: 1,
                    py: 0.2,
                    borderRadius: "5px",
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                  }}
                >
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      flexShrink: 0,
                    }}
                  />
                  {roleName}
                </Box>
              )}
            </Box>
          </Box>

          {/* Info card */}
          <Box
            sx={{
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(0,0,0,0.2)",
              backdropFilter: "blur(8px)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.07)",
            }}
          >
            {/* Login ID */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 0.9,
                borderBottom:
                  clientId || (isAdmin && planId)
                    ? "1px solid rgba(255,255,255,0.06)"
                    : "none",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexShrink: 0 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#90caf9", opacity: 0.7 }} />
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: "0.7px",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.4)",
                  }}
                >
                  Login ID
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#90caf9",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "62%",
                  textAlign: "right",
                  letterSpacing: "0.2px",
                }}
              >
                {userId}
              </Typography>
            </Box>

            {/* Client ID */}
            {clientId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.9,
                  borderBottom:
                    isAdmin && planId
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "none",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexShrink: 0 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#90caf9", opacity: 0.7 }} />
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.7px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.4)",
                    }}
                  >
                    Client ID
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#90caf9",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "62%",
                    textAlign: "right",
                    letterSpacing: "0.2px",
                  }}
                >
                  {clientId}
                </Typography>
              </Box>
            )}

            {/* Plan - Admin only */}
            {isAdmin && planId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.9,
                  background: "rgba(76, 175, 80, 0.07)",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexShrink: 0 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#81c784", opacity: 0.9 }} />
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.7px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.4)",
                    }}
                  >
                    Plan
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    background: "linear-gradient(135deg, rgba(76,175,80,0.25), rgba(56,142,60,0.25))",
                    border: "1px solid rgba(76,175,80,0.45)",
                    color: "#a5d6a7",
                    fontSize: "10px",
                    fontWeight: 700,
                    px: 1.1,
                    py: 0.2,
                    borderRadius: "20px",
                    letterSpacing: "0.2px",
                  }}
                >
                  ✦ {planName}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Trial / Interested user banner */}
        {userStatus === "Interested" && (
          <Box
            sx={{
              mx: 1.5,
              mt: 1.5,
              mb: 0.5,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LockIcon sx={{ color: "#fff", fontSize: 16, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem", lineHeight: 1.2 }}>
                Trial Mode
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.68rem", lineHeight: 1.3 }}>
                Subscribe to unlock all features
              </Typography>
            </Box>
          </Box>
        )}

        {/* Menu Items - Keep Original */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            pb: 2,
          }}
        >
          <MenuList
            roleId={roleId}
            isDarkTheme={isDarkTheme}
            handleMenuItemClick={handleMenuItemClick}
            userStatus={userStatus}
          />
        </Box>
      </Drawer>

      {/* Subscription Required Modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
      />

      {/* Confirmation Dialog for Logout - Add mobile responsiveness only here */}
      <Dialog
        open={openDialog}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            padding: isMobile ? "16px" : "24px", // Only adjust dialog padding
            borderRadius: "16px",
            backgroundColor: isDarkTheme ? "#333" : "#fff",
            color: isDarkTheme ? "#fff" : "#000",
            boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.3)",
            maxWidth: isMobile ? "90%" : "450px", // Only adjust dialog width
            width: "100%",
            margin: isMobile ? "16px" : "auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontWeight: "bold",
            fontSize: isMobile ? "18px" : "22px", // Only adjust font size
            color: isDarkTheme ? "#ff5722" : "#d32f2f",
            pb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: isMobile ? 40 : 48, // Only adjust icon container
              height: isMobile ? 40 : 48,
              borderRadius: "50%",
              background: isDarkTheme
                ? "rgba(244, 67, 54, 0.2)"
                : "rgba(244, 67, 54, 0.1)",
            }}
          >
            <LogoutIcon sx={{ fontSize: isMobile ? "24px" : "28px" }} />
          </Box>
          Confirm Logout
        </DialogTitle>

        {/* Simplified content: no TextField */}
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              fontSize: isMobile ? "14px" : "16px",
              color: isDarkTheme ? "#ddd" : "#333",
              lineHeight: 1.6,
            }}
          >
            You are about to log out from your session. Do you want to continue?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            padding: isMobile ? "12px 16px" : "16px 24px",
            pt: 3,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "30px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            disabled={isLoggingOut}
            fullWidth={isMobile}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "30px",
            }}
          >
            {isLoggingOut ? "Logging out..." : "Logout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResponsiveAppBar;
